import AdvancedDataTable from 'Components/Generic/AdvancedDataTable';
import DataTableHeaderTextFieldFilter from 'Components/Generic/AdvancedDataTable/DataTableHeaderTextFieldFilter';
import {
  TEXT_FILTER_MAP,
  TEXT_LIKE_FILTER_MAP,
} from 'Components/Generic/AdvancedDataTable/Helper';
import BOSSelect from 'Components/Generic/BOSSelect';
import QuickViewButton from 'Components/Generic/Buttons/QuickViewButton';
import StatusButton from 'Components/Generic/Buttons/StatusButton';
import VendorNameWithDrawerButton from 'Components/SiteDrawerContainer/VendorNameWithDrawerButton';
import { getObjectStatus, OBJECT_STATUS } from 'Data/Enum';
import SiteDrawerData from 'Data/SiteDrawerData';
import VendorData from 'Data/VendorData';
import DataManager from 'Lib/DataManager';
import { usePersistentFiltering } from 'Lib/hooks/usePersistentFiltering';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { getClickableLink } from 'Util';

let DM = new DataManager();

let filterOptions = {
  name: {
    seqRestKey: '*name',
    mapToSeqRestValue: TEXT_LIKE_FILTER_MAP,
  },
  contact_full_name: {
    seqRestKey: '*contact_full_name',
    mapToSeqRestValue: TEXT_FILTER_MAP,
  },
  website_url: {
    seqRestKey: '*website_url',
    mapToSeqRestValue: TEXT_FILTER_MAP,
  },
  status: {
    seqRestKey: '*status',
  },
};

const VendorListView = ({ addNewVendorButton }) => {
  const [data, setData] = useState();
  const [refreshKey, setRefreshKey] = useState(0);
  const [persistentFilter, updateFilterField] = usePersistentFiltering(
    `VEND_FILTER`,
    { status: [OBJECT_STATUS.ACTIVE] },
  );

  useEffect(() => {
    DM.add(VendorData.registerListener('LIST_LOADED', setData), VendorData);
    DM.add(
      VendorData.registerListener('LIST_LOAD_ERROR', onVendorListLoadError),
      VendorData,
    );
    DM.add(
      VendorData.registerListener('RECORD_CREATED', refreshData),
      VendorData,
    );
    DM.add(
      VendorData.registerListener('RECORD_UPDATED', refreshData),
      VendorData,
    );
    DM.add(
      VendorData.registerListener('RECORD_ACTIVATED', refreshAndCloseDrawer),
      VendorData,
    );
    DM.add(
      VendorData.registerListener('RECORD_DEACTIVATED', refreshAndCloseDrawer),
      VendorData,
    );
    return () => {
      DM.clear();
    };
  }, []);

  const onVendorListLoadError = () => {};

  const refreshData = () => {
    setRefreshKey((prevState) => prevState + 1);
  };

  const refreshAndCloseDrawer = () => {
    refreshData();
    SiteDrawerData.close();
  };

  const headers = [
    {
      label: 'Name',
      value: 'name',
      sortable: true,
      filterable: true,
      renderHeader() {
        return (
          <DataTableHeaderTextFieldFilter
            fieldName={'Name'}
            defaultValue={persistentFilter?.name}
            onFilterValueUpdate={(e) =>
              updateFilterField('name', e.target.value)
            }
          />
        );
      },
      render(vendor) {
        return <Link to={`./${vendor.id}`}>{vendor.name}</Link>;
      },
    },

    {
      label: 'Website',
      value: 'website_url',
      filterable: true,
      renderHeader() {
        return (
          <DataTableHeaderTextFieldFilter
            fieldName={'Website'}
            defaultValue={persistentFilter?.website_url}
            onFilterValueUpdate={(e) =>
              updateFilterField('website_url', e.target.value)
            }
          />
        );
      },
      render(vendor) {
        return (
          <a
            href={getClickableLink(vendor.website_url)}
            target='_blank'
            rel='noreferrer'
            onMouseDown={(e) => e.preventDefault()}>
            {vendor.website_url}
          </a>
        );
      },
    },

    {
      label: 'Contact Name',
      value: 'contact_full_name',
      width: 260,
      sortable: true,
      filterable: true,
      renderHeader() {
        return (
          <DataTableHeaderTextFieldFilter
            fieldName={'Contact Name'}
            defaultValue={persistentFilter?.contact_full_name}
            onFilterValueUpdate={(e) =>
              updateFilterField('contact_full_name', e.target.value)
            }
          />
        );
      },
    },

    {
      label: 'Phone',
      value: 'contact_phone',
      fieldName: 'contact_phone_numbers',
      width: 200,
      format: (phone_json) => {
        return phone_json.phone;
      },
    },

    // {
    //   label: 'Fax',
    //   value: 'contact_fax',
    //   fieldName: 'contact_phone_numbers',
    //   format: (phone_json) => {
    //     return phone_json.fax;
    //   },
    // },

    {
      label: 'Email',
      value: 'contact_emails',
      width: 220,
      render(vendor) {
        return (
          <a
            href={`mailto:${vendor.contact_emails}`}
            onMouseDown={(e) => e.preventDefault()}>
            {vendor.contact_emails}
          </a>
        );
      },
    },

    {
      label: 'Status',
      value: 'status',
      width: 120,
      centeredHeader: true,
      filterable: true,
      renderHeader() {
        return (
          <BOSSelect
            listData={Object.values(OBJECT_STATUS)}
            idField={'key'}
            labelField={'label'}
            labelText='Status'
            onItemSelect={(item) => updateFilterField('status', [item])}
            defaultSelectedId={
              persistentFilter?.status?.length
                ? persistentFilter.status[0].key
                : undefined
            }
            isHeader
          />
        );
      },
      format: (status) => {
        return <StatusButton statusKey={status} />;
      },
      getText: (vendor) =>
        getObjectStatus(vendor.status)?.label || vendor.status,
    },

    {
      label: '',
      value: 'quick_view',
      isIconCol: true,
      render(vendor) {
        return (
          <VendorNameWithDrawerButton
            editable
            vendorId={vendor.id}
            label={<QuickViewButton />}
          />
        );
      },
    },
  ];

  const isVendorInactive = (vendor) => {
    return vendor?.status === OBJECT_STATUS.DEACTIVATED.key || false;
  };

  return (
    <AdvancedDataTable
      headers={headers}
      refreshKey={refreshKey}
      title={'Manage Vendors'}
      dataKey={'VEND'}
      data={data}
      dataQueryFunc={VendorData.getRecordList}
      filterOptions={filterOptions}
      filterBy={persistentFilter}
      updateFilterField={updateFilterField}
      defaultSort={'name'}
      actionButtons={addNewVendorButton}
      highlightFunc={isVendorInactive}
    />
  );
};

VendorListView.propTypes = {
  addNewVendorButton: PropTypes.node,
};

export default VendorListView;
